<template>
  <div 
    @click="handleClick"
    @auxclick="handleAuxClick"
    @contextmenu="handleRightClick"
    class="grid cursor-pointer grid-cols-12 items-center gap-4 px-4 py-3.5 transition-colors"
    :class="[
      isSelected ? 'bg-blue-50 shadow-[inset_3px_0_0_0_#0ea5e9]' : 'hover:bg-slate-50',
      !isLast ? 'border-b border-slate-100' : ''
    ]"
  >
    <!-- Fixture ID -->
    <div class="col-span-1">
      <span class="text-sm font-semibold text-rose-600">#{{ fixture.id }}</span>
    </div>

    <!-- Time -->
    <div class="col-span-1">
      <span class="rounded-md bg-slate-100 px-1.5 py-0.5 text-xs font-semibold tabular-nums text-slate-700">{{ extractTime(fixture.gamedate) }}</span>
    </div>

    <!-- Teams/Name -->
    <div class="col-span-4">
      <div v-if="type === 'fixture'" class="flex min-w-0 items-center">
        <span class="truncate text-sm font-medium text-slate-900">{{ fixture.home_team?.team_name || fixture.home_team?.name || 'Unknown Team' }}</span>
        <span class="mx-2 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-slate-400">vs</span>
        <span class="truncate text-sm font-medium text-slate-900">{{ fixture.away_team?.team_name || fixture.away_team?.name || 'Unknown Team' }}</span>
      </div>
      <div v-else class="flex items-center">
        <span class="truncate text-sm font-medium text-slate-900">{{ fixture.name || 'Unknown Outright' }}</span>
      </div>
    </div>

    <!-- League -->
    <div class="col-span-2">
      <span class="truncate text-sm text-slate-500">{{ fixture.league?.league_name || fixture.league?.name || 'Unknown League' }}</span>
    </div>

    <!-- Status -->
    <div class="col-span-2">
      <span 
        :class="getStatusClasses(fixture.status)"
        class="inline-block rounded-full px-2.5 py-1 text-[11px] font-medium"
      >
        {{ fixture.status || 'Scheduled' }}
      </span>
    </div>

    <!-- Actions -->
    <div class="col-span-2 flex items-center justify-end gap-1.5">
      <!-- View Button -->
      <button
        @click.stop="handleClick"
        @auxclick.stop="handleAuxClick"
        class="rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-slate-700"
        title="Click to open, Ctrl+Click for new tab"
      >
        View
      </button>
      
      <!-- New Tab Button -->
      <button
        @click.stop="openInNewTab"
        class="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
        title="Open in new tab"
      >
        <i class="fas fa-external-link-alt text-sm"></i>
      </button>
      
      <button
        @click.stop
        class="rounded-lg p-1.5 text-slate-300 transition-colors hover:bg-rose-50 hover:text-rose-500"
        title="Delete fixture"
      >
        <i class="fas fa-trash text-sm"></i>
      </button>
    </div>

    <!-- Context Menu -->
    <div 
      v-if="showContextMenu"
      @click.stop
      class="fixed z-50 rounded-xl border border-slate-200 bg-white py-1.5 shadow-xl"
      :style="{ left: contextMenuX + 'px', top: contextMenuY + 'px' }"
    >
      <button
        @click="openInCurrentTab"
        class="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-slate-50"
      >
        <i class="fas fa-eye text-sky-500"></i>
        Open {{ type === 'fixture' ? 'fixture' : 'outright' }}
      </button>
      <button
        @click="openInNewTab"
        class="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-slate-50"
      >
        <i class="fas fa-external-link-alt text-sky-500"></i>
        Open in new tab
      </button>
      <hr class="my-1 border-slate-100">
      <button
        @click="copyLink"
        class="flex w-full items-center gap-2 px-4 py-2 text-left text-sm hover:bg-slate-50"
      >
        <i class="fas fa-copy text-gray-500"></i>
        Copy link
      </button>
    </div>
  </div>
</template>

<script setup>
import { defineProps, defineEmits, ref, onMounted, onUnmounted } from 'vue'

const props = defineProps({
  fixture: {
    type: Object,
    required: true
  },
  isSelected: {
    type: Boolean,
    default: false
  },
  isLast: {
    type: Boolean,
    default: false
  },
  type: {
    type: String,
    default: 'fixture', // 'fixture' or 'outright'
    validator: (value) => ['fixture', 'outright'].includes(value)
  }
})

const emit = defineEmits(['select'])

const showContextMenu = ref(false)
const contextMenuX = ref(0)
const contextMenuY = ref(0)

const handleClick = (event) => {
  if (event.ctrlKey || event.metaKey) {
    // Ctrl+Click or Cmd+Click - open in new tab
    openInNewTab()
  } else {
    // Regular click - open in current tab
    openInCurrentTab()
  }
}

const handleAuxClick = (event) => {
  // Middle mouse button click - open in new tab
  if (event.button === 1) {
    event.preventDefault()
    openInNewTab()
  }
}

const handleRightClick = (event) => {
  event.preventDefault()
  contextMenuX.value = event.clientX
  contextMenuY.value = event.clientY
  showContextMenu.value = true
}

const openInCurrentTab = () => {
  hideContextMenu()
  emit('select', props.fixture)
}

const openInNewTab = () => {
  hideContextMenu()
  const url = props.type === 'fixture' ? `/fixture/${props.fixture.id}` : `/outrights/${props.fixture.id}`
  window.open(url, '_blank', 'noopener,noreferrer')
}

const copyLink = async () => {
  hideContextMenu()
  const url = `${window.location.origin}${props.type === 'fixture' ? `/fixture/${props.fixture.id}` : `/outrights/${props.fixture.id}`}`
  try {
    await navigator.clipboard.writeText(url)
    // You could add a toast notification here
    console.log('Link copied to clipboard')
  } catch (err) {
    console.error('Failed to copy link:', err)
  }
}

const hideContextMenu = () => {
  showContextMenu.value = false
}

const handleClickOutside = (event) => {
  if (showContextMenu.value) {
    hideContextMenu()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})

const extractTime = (gamedate) => {
  if (!gamedate) return '00:00'
  
  // Handle European format
  if (gamedate.includes('.')) {
    const [datePart, timePart = '00:00'] = gamedate.split(' ')
    return timePart.substring(0, 5) // Ensure HH:mm format
  }
  
  // Fallback for other formats
  try {
    const dateObj = new Date(gamedate.replace(' ', 'T'))
    if (isNaN(dateObj.getTime())) return '00:00'
    return dateObj.toLocaleTimeString('et-EE', { hour: '2-digit', minute: '2-digit', hour12: false })
  } catch (error) {
    return '00:00'
  }
}

const getStatusClasses = (status) => {
  const classes = {
    'Active': 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100',
    'Scheduled': 'bg-sky-50 text-sky-700 ring-1 ring-sky-100',
    'Completed': 'bg-slate-100 text-slate-600 ring-1 ring-slate-200'
  }
  return classes[status] || 'bg-slate-100 text-slate-600 ring-1 ring-slate-200'
}
</script> 